import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { createT, resolveLanguage } from './translations';
import { collectDecisionsBefore, getPickableChapters } from './utils/quizDecisions';
import BubbleBackground from './BubbleBackground';

/**
 * Restore-progress quiz. Launched from the Saves screen when the
 * player wants to continue from a chapter they played in a previous
 * version of the game but has no valid save.
 *
 * Slides:
 *   1. Chapter picker
 *   2. Decision-by-decision quiz for every prior chapter
 * On completion calls onComplete({ targetChapterNumber, decisions, lang }).
 * On cancel (back/close from the picker) calls onCancel().
 *
 * Each slide animates out upward; next slide slides up from below.
 */

const SLIDE_DURATION = 0.35;
const HIDE_SCROLLBAR = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none'
};

function Slide({ slideKey, direction = 1, children }) {
  return (
    <motion.div
      key={slideKey}
      initial={{ y: direction > 0 ? '40%' : '-40%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: direction > 0 ? '-40%' : '40%', opacity: 0 }}
      transition={{ duration: SLIDE_DURATION, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 overflow-y-auto overscroll-contain"
      style={HIDE_SCROLLBAR}
    >
      <style>{`.onboarding-slide::-webkit-scrollbar{display:none}`}</style>
      <div className="onboarding-slide min-h-full flex flex-col px-6 pt-14 pb-10 max-w-md mx-auto">
        {children}
      </div>
    </motion.div>
  );
}

function QuestionHeader({ chapterLabel, title, hint }) {
  return (
    <div className="flex flex-col gap-2 mb-8">
      {chapterLabel ? (
        <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-violet-700">{chapterLabel}</div>
      ) : null}
      <h1 className="text-[19px] font-bold text-slate-900 leading-snug">{title}</h1>
      {hint ? <p className="text-[13px] text-slate-600 leading-relaxed">{hint}</p> : null}
    </div>
  );
}

function AnswerButton({ label, onClick, leading }) {
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full rounded-2xl bg-white ring-1 ring-slate-200 shadow-[0_6px_20px_-8px_rgba(71,45,140,0.18)] px-4 py-4 flex items-center gap-3 text-left text-[15px] font-semibold text-slate-900 hover:ring-violet-500 hover:shadow-[0_10px_24px_-8px_rgba(124,58,237,0.35)] transition-all"
    >
      {leading ? <div className="shrink-0">{leading}</div> : null}
      <span className="min-w-0 flex-1">{label}</span>
      <svg viewBox="0 0 24 24" className="shrink-0 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </motion.button>
  );
}

function NavButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-slate-500 hover:text-slate-900 self-start mb-4 transition-colors font-medium"
    >
      ← {label}
    </button>
  );
}

function ChapterCover({ url, number }) {
  if (url) {
    return (
      <div className="h-12 w-12 rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200">
        <img src={url} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }
  return (
    <div className="h-12 w-12 rounded-xl bg-violet-200/80 flex items-center justify-center text-violet-900 font-bold text-base ring-1 ring-white/60">
      {number ?? '?'}
    </div>
  );
}

export default function OnboardingQuiz({ allStoryflows, language, onComplete, onCancel }) {
  const resolvedLang = useMemo(() => resolveLanguage(language || 'auto'), [language]);
  const t = useMemo(() => createT(resolvedLang), [resolvedLang]);

  const pickableChapters = useMemo(() => getPickableChapters(allStoryflows), [allStoryflows]);

  const [stack, setStack] = useState([{ kind: 'chapterPicker' }]);
  const [direction, setDirection] = useState(1);
  const [targetChapterNumber, setTargetChapterNumber] = useState(null);
  const [decisions, setDecisions] = useState({});

  const decisionsToAsk = useMemo(() => {
    if (targetChapterNumber == null) return [];
    return collectDecisionsBefore(allStoryflows, targetChapterNumber);
  }, [allStoryflows, targetChapterNumber]);

  const push = useCallback((step) => {
    setDirection(1);
    setStack((prev) => [...prev, step]);
  }, []);
  const pop = useCallback(() => {
    if (stack.length <= 1) { onCancel?.(); return; }
    setDirection(-1);
    setStack((prev) => prev.slice(0, -1));
  }, [stack.length, onCancel]);

  const top = stack[stack.length - 1];

  const finish = useCallback((result) => { onComplete(result); }, [onComplete]);

  const answerDecision = useCallback((decisionName, option) => {
    const nextDecisions = { ...decisions, [decisionName]: option };
    setDecisions(nextDecisions);
    const currentIdx = top.kind === 'decision' ? top.index : -1;
    const nextIdx = currentIdx + 1;
    if (nextIdx < decisionsToAsk.length) {
      push({ kind: 'decision', index: nextIdx });
    } else {
      finish({
        targetChapterNumber,
        decisions: nextDecisions,
        lang: resolvedLang
      });
    }
  }, [decisions, decisionsToAsk, finish, push, resolvedLang, targetChapterNumber, top]);

  const handlePickChapter = (chapter) => {
    const chapterNum = chapter.number ?? 0;
    setTargetChapterNumber(chapterNum);
    const toAsk = collectDecisionsBefore(allStoryflows, chapterNum);
    if (toAsk.length === 0) {
      finish({ targetChapterNumber: chapterNum, decisions: {}, lang: resolvedLang });
      return;
    }
    push({ kind: 'decision', index: 0 });
  };

  const renderTop = () => {
    if (top.kind === 'chapterPicker') {
      return (
        <Slide slideKey="chapterPicker" direction={direction}>
          <NavButton onClick={pop} label={t('quizBack')} />
          <QuestionHeader title={t('quizRestoreTitle')} hint={t('quizPickChapterHint')} />
          {pickableChapters.length === 0 ? (
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 px-4 py-6 text-[13px] text-slate-600 text-center">
              {t('quizNoChaptersAvailable')}
            </div>
          ) : (
            <div className="flex flex-col gap-2.5 pb-2">
              {pickableChapters.map((chapter) => (
                <AnswerButton
                  key={chapter.id}
                  label={chapter.name || `Chapter ${chapter.number ?? '?'}`}
                  onClick={() => handlePickChapter(chapter)}
                  leading={<ChapterCover url={chapter.cover?.url128 || chapter.cover?.url64 || null} number={chapter.number} />}
                />
              ))}
            </div>
          )}
        </Slide>
      );
    }
    if (top.kind === 'decision') {
      const dec = decisionsToAsk[top.index];
      if (!dec) return null;
      const chapterLabel = `${dec.chapterName || `Ch.${dec.chapterNumber}`} · ${top.index + 1}/${decisionsToAsk.length}`;
      return (
        <Slide slideKey={`decision-${top.index}`} direction={direction}>
          <NavButton onClick={pop} label={t('quizBack')} />
          <QuestionHeader chapterLabel={chapterLabel} title={dec.decisionName} />
          <div className="flex flex-col gap-2.5 pb-2">
            {(dec.options || []).map((opt) => (
              <AnswerButton
                key={opt}
                label={opt}
                onClick={() => answerDecision(dec.decisionName, opt)}
              />
            ))}
          </div>
        </Slide>
      );
    }
    return null;
  };

  return (
    <div
      className="game-shell h-full w-full font-game select-none relative overflow-hidden"
      onContextMenu={(e) => e.preventDefault()}
    >
      <BubbleBackground />
      <div className="absolute inset-0">
        <AnimatePresence mode="wait" initial={false}>
          {renderTop()}
        </AnimatePresence>
      </div>
    </div>
  );
}
